"use client";

import { useCallback, useMemo, useEffect, useState, useRef } from "react";
import Image from "next/image";
import { Icon } from "@/components/ui/icon";
import { IconAvatar } from "@/components/ui/icon-avatar";
import { Select } from "@/components/ui/select";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useGetProject, listEmployers, webImport } from "@/lib/api/generated";
import { showToast } from "@/components/ui/sonner";
import { useJobWizardStore } from "@/features/jobs/store/job-store";
import { AtsImportFields } from "./ats-import-fields";
import { AtsImportDescription } from "./ats-import-description";
import { MAX_JOB_DESCRIPTION_LENGTH } from "./constants";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { UserSelect } from "@/components/ui/user-select";
import { RichTextEditor } from "@/components/ui/editor/rich-text-editor";
import { WebImport, WebImportButton } from "@/components/ui/web-import";
import { useMembers } from "@/features/settings/hooks/use-members";
import { useAuth } from "@/features/auth";
import { CreateEmployerModal } from "@/features/settings/components/employers/list/create-employer-modal";
import { ProjectSelect } from "@/components/layout/navbar/project-select";
import type { EmployerResponse } from "@/lib/api/model";

function EmployerLogo({
  coverUrl,
  name,
}: {
  coverUrl?: string | null;
  name?: string;
}) {
  return (
    <IconAvatar
      src={coverUrl}
      size={20}
      alt={name ?? ""}
      className="shrink-0 rounded-[3px]"
      icon={<Icon name="IconBag" className="text-heading opacity-50" />}
    />
  );
}

function EmployerSelect({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (id: string | null) => void;
}) {
  const queryClient = useQueryClient();
  const { data, isFetching } = useQuery({
    queryKey: ["employers"],
    queryFn: () => listEmployers({ per_page: 100 }),
  });
  const employers = data?.employers ?? [];
  const selected = employers.find((e) => e.id === value) ?? null;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showCreate, setShowCreate] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (!value && employers.length > 0) {
      const defaultEmployer =
        employers.find((e) => e.is_default) ?? employers[0];
      onChange(defaultEmployer.id);
    }
  }, [employers, value, onChange]);

  const filtered = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (!q) return employers;
    return employers.filter((e) => e.name.toLowerCase().includes(q));
  }, [employers, debouncedQuery]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [filtered]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => inputRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, [open]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) {
      setQuery("");
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    }
  }, []);

  const handleSelect = useCallback(
    (id: string) => {
      onChange(id);
      setOpen(false);
      setQuery("");
      setDebouncedQuery("");
      setHighlightedIndex(-1);
    },
    [onChange],
  );

  const handleCreated = useCallback(
    (employer: EmployerResponse) => {
      queryClient.invalidateQueries({ queryKey: ["employers"] });
      onChange(employer.id);
    },
    [queryClient, onChange],
  );

  const openCreate = useCallback(() => {
    setOpen(false);
    setShowCreate(true);
  }, []);

  const showPending = isFetching && employers.length === 0;
  const showEmpty = !showPending && filtered.length === 0;
  const hasNoEmployers = !isFetching && employers.length === 0;

  return (
    <>
      {hasNoEmployers ? (
        <button
          type="button"
          onClick={openCreate}
          className="flex items-center gap-2 h-9 w-full rounded-lg border border-dashed border-border bg-contrast/[0.02] px-3 text-sm text-contrast/50 hover:bg-contrast/6 hover:border-contrast/20 transition-colors"
        >
          <Icon
            name="IconPlusMedium"
            size={14}
            className="text-heading opacity-40"
          />
          <span className="truncate">Create an employer</span>
        </button>
      ) : (
        <Select.Root
          open={open}
          onOpenChange={handleOpenChange}
          highlightedIndex={highlightedIndex}
          onHighlightedIndexChange={setHighlightedIndex}
        >
          <Select.Trigger>
            <div className="flex items-center gap-2 flex-1 min-w-0">
              {selected ? (
                <EmployerLogo
                  coverUrl={selected.cover_url}
                  name={selected.name}
                />
              ) : (
                <div className="opacity-40">
                  <Icon name="IconBag" size={14} className="text-heading" />
                </div>
              )}
              <span
                className={`flex-1 text-left truncate text-sm ${selected ? "text-heading" : "text-contrast/50"}`}
              >
                {selected?.name ?? "Select employer..."}
              </span>
              <div className="opacity-40">
                <Icon
                  name="IconChevronGrabberVertical"
                  size={14}
                  className="text-heading"
                />
              </div>
            </div>
          </Select.Trigger>

          <Select.Content>
            <Select.Search
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search..."
              loading={isFetching}
            />

            <Select.List>
              {showPending ? (
                <Select.ListPending />
              ) : showEmpty ? (
                <div className="flex flex-col items-center justify-center gap-1 py-8 px-3">
                  <div className="opacity-30 mb-1">
                    <Icon name="IconBag" size={32} className="text-heading" />
                  </div>
                </div>
              ) : (
                filtered.map((emp, i) => {
                  const isSelected = emp.id === value;
                  return (
                    <Select.Item
                      key={emp.id}
                      index={i}
                      selected={isSelected}
                      onSelect={() => handleSelect(emp.id)}
                    >
                      <Select.ItemIcon>
                        <EmployerLogo
                          coverUrl={emp.cover_url}
                          name={emp.name}
                        />
                      </Select.ItemIcon>
                      <Select.ItemLabel selected={isSelected}>
                        {emp.name}
                      </Select.ItemLabel>
                      {isSelected && <Select.ItemCheck />}
                    </Select.Item>
                  );
                })
              )}
            </Select.List>

            <Select.Footer>
              <Button
                type="button"
                variant="primary"
                size="sm"
                className="flex items-center gap-1"
                onClick={openCreate}
              >
                <Icon name="IconPlusMedium" size={14} />
                Create employer
              </Button>
            </Select.Footer>
          </Select.Content>
        </Select.Root>
      )}

      <CreateEmployerModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={handleCreated}
      />
    </>
  );
}

export function StepDetails() {
  const {
    form,
    updateForm,
    mode,
    cloneTargetProjectId,
    setCloneTargetProjectId,
    createdJobId,
  } = useJobWizardStore();
  const isCloneMode = mode === "clone";
  const destinationLocked = Boolean(createdJobId);
  const scratchDescriptionRef = useRef(form.description);
  const atsDescriptionRef = useRef(
    form.sourcingType === "ats_import" ? form.description : "",
  );

  useEffect(() => {
    if (form.sourcingType === "scratch") {
      scratchDescriptionRef.current = form.description;
    } else {
      atsDescriptionRef.current = form.description;
    }
  }, [form.sourcingType, form.description]);

  const setSourcingType = (next: "scratch" | "ats_import") => {
    if (next === form.sourcingType) return;
    const outgoing = form.description;
    const incoming =
      next === "ats_import"
        ? atsDescriptionRef.current
        : scratchDescriptionRef.current;
    if (next === "ats_import") {
      scratchDescriptionRef.current = outgoing;
    } else {
      atsDescriptionRef.current = outgoing;
    }
    useJobWizardStore.setState((s) => ({
      form: { ...s.form, sourcingType: next, description: incoming },
    }));
  };

  const { data: membersData } = useMembers({ per_page: 100, status: "active" });
  const { user, selectedProjectId } = useAuth();

  // Clone destination project, or the globally selected project for create.
  // Fetched by id rather than found in /api/projects: that list is paginated
  // (per_page defaults to 20, newest first) and omits `collaborators`, so the
  // eligibility filter below saw an empty collaborator set for every project.
  const scopeProjectId = isCloneMode ? cloneTargetProjectId : selectedProjectId;
  const { data: scopeProject = null } = useGetProject(scopeProjectId ?? "", {
    query: { enabled: !!scopeProjectId, staleTime: 30_000 },
  });

  // If clone opened without a project id (or source project missing), fall back
  // to the currently selected project.
  useEffect(() => {
    if (!isCloneMode || cloneTargetProjectId || !selectedProjectId) return;
    setCloneTargetProjectId(selectedProjectId);
  }, [
    isCloneMode,
    cloneTargetProjectId,
    selectedProjectId,
    setCloneTargetProjectId,
  ]);

  const eligibleMembers = useMemo(() => {
    const members = membersData?.members ?? [];
    if (!scopeProject || scopeProject.access_level === "shared") return members;
    const collabUserIds = new Set(
      (scopeProject.collaborators ?? []).map((c) => c.user_id),
    );
    return members.filter(
      (m) =>
        m.role === "admin" ||
        m.id === scopeProject.created_by ||
        collabUserIds.has(m.id),
    );
  }, [membersData?.members, scopeProject]);

  const eligibleIds = useMemo(
    () => new Set(eligibleMembers.map((m) => m.id)),
    [eligibleMembers],
  );

  useEffect(() => {
    if (eligibleMembers.length === 0) return;
    if (
      form.managerId &&
      !eligibleMembers.some((m) => m.id === form.managerId)
    ) {
      updateForm("managerId", null);
      return;
    }
    if (
      !form.managerId &&
      user &&
      eligibleMembers.some((m) => m.id === user.id)
    ) {
      updateForm("managerId", user.id);
    }
  }, [eligibleMembers, form.managerId, updateForm, user]);

  const cloneDisplayProject = useMemo(() => {
    if (!scopeProject) return null;
    return {
      id: scopeProject.id,
      title: scopeProject.title,
      cover_image_url: scopeProject.cover_image_url ?? null,
    };
  }, [scopeProject]);

  const titleImportMutation = useMutation({
    mutationFn: (url: string) => webImport({ url, mode: "job_details" }),
    onSuccess: (result) => {
      const importedTitle = result.job_title || result.project_title;
      if (importedTitle) updateForm("title", importedTitle);
      if (result.description && !form.description.trim()) {
        updateForm("description", result.description);
      }
      showToast.success("Imported successfully");
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to import";
      showToast.error("Import failed", { description: message });
    },
  });

  const descImportMutation = useMutation({
    mutationFn: (url: string) => webImport({ url, mode: "job_details" }),
    onSuccess: (result) => {
      if (result.description) {
        updateForm("description", result.description);
      }
      if (!form.title.trim()) {
        const importedTitle = result.job_title || result.project_title;
        if (importedTitle) updateForm("title", importedTitle);
      }
      showToast.success("Description imported");
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : "Failed to import";
      showToast.error("Import failed", { description: message });
    },
  });

  const handleTitleImport = useCallback(
    async (url: string) => {
      await titleImportMutation.mutateAsync(url);
    },
    [titleImportMutation],
  );

  const handleDescImport = useCallback(
    async (url: string) => {
      await descImportMutation.mutateAsync(url);
    },
    [descImportMutation],
  );

  const handleGenerate = useCallback(() => {
    showToast.info("Coming soon", {
      description: "AI description generation is not available yet.",
    });
  }, []);

  const descriptionLength = form.description.length;
  const descriptionOverLimit = descriptionLength > MAX_JOB_DESCRIPTION_LENGTH;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex gap-3 w-full">
        <button
          type="button"
          onClick={() => setSourcingType("scratch")}
          className={`flex flex-1 items-center gap-2.5 p-2.5 rounded-lg border transition-colors cursor-pointer overflow-clip shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] ${
            form.sourcingType === "scratch"
              ? "bg-contrast/8 border-contrast/[0.15]"
              : "bg-contrast/[0.02] border-contrast/4 hover:bg-contrast/6"
          }`}
        >
          <div className="relative size-12 shrink-0 rounded-lg overflow-hidden">
            <div
              className="absolute inset-0 rounded-lg border border-border"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, rgba(255,175,83,0) 0%, rgba(255,175,83,0.2) 100%), linear-gradient(90deg, rgb(33,33,35) 0%, rgb(33,33,35) 100%)",
                backgroundOrigin: "border-box",
              }}
            />
            <div className="absolute left-1/2 top-[calc(50%+0.5px)] -translate-x-1/2 -translate-y-1/2 w-[26px] h-[29px]">
              <Image
                src="/assets/jobs/from-scratch.svg"
                alt=""
                width={26}
                height={29}
                className="block size-full"
                draggable={false}
              />
            </div>
          </div>
          <div className="flex flex-col items-start text-left flex-1 min-w-0 gap-0.5">
            <span className="text-sm text-heading leading-5">From Scratch</span>
            <span className="text-sm text-contrast/60">
              Upload your profiles
            </span>
          </div>
          {form.sourcingType === "scratch" && (
            <Icon
              name="IconCheckCircle2"
              size={16}
              className="text-heading shrink-0"
            />
          )}
        </button>

        {/* Import with an ATS */}
        <button
          type="button"
          onClick={() => setSourcingType("ats_import")}
          className={`flex flex-1 items-center gap-2.5 p-2.5 rounded-lg border transition-colors cursor-pointer overflow-clip shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] ${
            form.sourcingType === "ats_import"
              ? "bg-contrast/8 border-contrast/[0.15]"
              : "bg-contrast/[0.02] border-contrast/4 hover:bg-contrast/6"
          }`}
        >
          <div className="relative size-12 shrink-0 rounded-lg overflow-hidden">
            <div
              className="absolute inset-0 rounded-lg border border-border"
              style={{
                backgroundImage:
                  "linear-gradient(180deg, rgba(43,93,243,0) 0%, rgba(43,93,243,0.1) 100%), linear-gradient(90deg, rgb(33,33,35) 0%, rgb(33,33,35) 100%)",
                backgroundOrigin: "border-box",
              }}
            />
            <div className="absolute left-1/2 top-[calc(50%+0.5px)] -translate-x-1/2 -translate-y-1/2 w-[29.376px] h-[21px]">
              <Image
                src="/assets/jobs/from-ats.svg"
                alt=""
                width={29.376}
                height={21}
                className="block size-full"
                draggable={false}
              />
            </div>
          </div>
          <div className="flex flex-col items-start text-left flex-1 min-w-0 gap-0.5">
            <span className="text-sm text-heading leading-5">
              Import with an ATS
            </span>
            <span className="text-sm text-contrast/60">
              From your integrations
            </span>
          </div>
          {form.sourcingType === "ats_import" && (
            <Icon
              name="IconCheckCircle2"
              size={16}
              className="text-heading shrink-0"
            />
          )}
        </button>
      </div>

      {isCloneMode && (
        <div className="flex flex-col gap-0.5">
          <Label className="gap-0.5">
            Project<span className="text-destructive">*</span>
          </Label>
          <ProjectSelect
            value={cloneTargetProjectId}
            onValueChange={(projectId) => setCloneTargetProjectId(projectId)}
            displayProject={cloneDisplayProject}
            size="default"
            variant="default"
            align="start"
            matchTriggerWidth
            showCreate={false}
            disabled={destinationLocked}
          />
        </div>
      )}

      {form.sourcingType === "ats_import" ? (
        <AtsImportFields />
      ) : (
        /* Job Title */
        <div className="flex flex-col gap-0.5">
          <Label className="gap-0.5">
            Job Title<span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <Input
              type="text"
              value={form.title}
              onChange={(e) => updateForm("title", e.target.value)}
              placeholder="e.g. Senior Software Engineer"
              variant="default"
              className="pr-[122px]"
            />
            <div className="absolute right-0 top-0 bottom-0 flex items-center pr-2 z-10">
              <WebImport
                onImport={handleTitleImport}
                isLoading={titleImportMutation.isPending}
              >
                <WebImportButton />
              </WebImport>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 min-[400px]:grid-cols-2 gap-3">
        <div className="flex flex-col gap-0.5">
          <Label className="gap-0.5">
            Job Manager<span className="text-destructive">*</span>
          </Label>
          <UserSelect
            value={form.managerId}
            onChange={(id) => updateForm("managerId", id)}
            placeholder="Select manager..."
            filter={(m) => eligibleIds.has(m.id)}
          />
        </div>
        <div className="flex flex-col gap-0.5">
          <Label className="gap-0.5">
            Employer<span className="text-destructive">*</span>
          </Label>
          <EmployerSelect
            value={form.employerId}
            onChange={(id) => updateForm("employerId", id)}
          />
        </div>
      </div>

      {form.sourcingType === "ats_import" && <AtsImportDescription />}

      {/* Job Description — scratch only; ATS imports inherit the remote job's description */}
      {form.sourcingType === "scratch" && (
        <div className="flex flex-col gap-0.5">
          <Label className="gap-0.5">
            Job Description<span className="text-destructive">*</span>
          </Label>
          <div className="relative">
            <div className="rounded-lg border border-border overflow-hidden shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
              <RichTextEditor
                content={form.description}
                onChange={(html) => updateForm("description", html)}
                placeholder="Describe the role, responsibilities, and key requirements..."
                wrapperClassName="bg-contrast/6 overflow-hidden"
                maxHeight="160px"
              />
              <div className="flex items-center justify-between bg-contrast/[0.02] border-t border-border p-2">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleGenerate}
                    className="flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <Icon name="IconMagicWand2" size={16} className="text-ai" />
                    <span className="bg-shine text-shine animate-bg-shine text-sm font-medium [--gradient-color:var(--ai)]">
                      Generate
                    </span>
                  </button>
                  <div className="w-px h-[17px] bg-contrast/[0.22]" />
                  <WebImport
                    onImport={handleDescImport}
                    isLoading={descImportMutation.isPending}
                  >
                    <WebImportButton />
                  </WebImport>
                </div>
                <button
                  type="button"
                  onClick={() => updateForm("description", "")}
                  className="text-sm text-contrast/50 hover:text-contrast/70 transition-colors cursor-pointer"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-start justify-between gap-3 mt-2">
            <p className="text-xs text-contrast/40 leading-4">
              Improves outreach quality and candidate response accuracy.
            </p>
            <span
              className={`shrink-0 text-xs leading-4 tabular-nums ${
                descriptionOverLimit ? "text-destructive" : "text-contrast/40"
              }`}
            >
              {descriptionLength.toLocaleString()} /{" "}
              {MAX_JOB_DESCRIPTION_LENGTH.toLocaleString()}
            </span>
          </div>
          {descriptionOverLimit && (
            <p className="text-xs text-destructive leading-4 mt-1">
              Description is too long. Please remove{" "}
              {(
                descriptionLength - MAX_JOB_DESCRIPTION_LENGTH
              ).toLocaleString()}{" "}
              character
              {descriptionLength - MAX_JOB_DESCRIPTION_LENGTH === 1 ? "" : "s"}.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
